import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, CreditCard, Tag, Users, LogOut, Menu, X, 
  Save, Loader2, Plus, Trash2, Edit, Check, AlertCircle, CheckCircle,
  Mail, Link, Server, Eye, EyeOff, Image, Video, Monitor, Shield, RefreshCw, BookOpen, Globe, Gauge, BarChart3, Receipt
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import BondAnimatedLogo from '../components/BondAnimatedLogo';
import AdminPortalTab from '../components/AdminPortalTab';
import AdminClients from '../components/AdminClients';
import { AdminMfa, AdminBiometricCard, AdminPasskeyPrompt } from '../components/AdminBiometric';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [activeTab, setActiveTab] = useState('clients');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mfa, setMfa] = useState(null);

  // Data states
  const [settings, setSettings] = useState({});
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Check stored admin token
  useEffect(() => {
    const storedToken = localStorage.getItem('insight_admin_token');
    if (storedToken) {
      setAdminToken(storedToken);
      verifyAdminToken(storedToken);
    }
  }, []);

  const verifyAdminToken = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      setSettings(response.data);
      loadAllData(token);
    } catch (error) {
      setIsAuthenticated(false);
      localStorage.removeItem('insight_admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      
      if (response.data.mfa_required) { setMfa(response.data); return; }
      finishLogin(response.data.token);
    } catch (error) {
      const st = error.response?.status;
      const detail = error.response?.data?.detail;
      setLoginError(st === 429 ? (detail || 'Troppi tentativi: riprova tra qualche minuto') : st === 503 ? (detail || 'MFA non configurata') : 'Username o password non validi');
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = (token) => {
    setMfa(null);
    setAdminToken(token);
    localStorage.setItem('insight_admin_token', token);
    setIsAuthenticated(true);
    loadAllData(token);
    toast.success('Login effettuato!');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminToken('');
    setUsername('');
    setPassword('');
    localStorage.removeItem('insight_admin_token');
  };

  const loadAllData = async (token) => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [settingsRes, plansRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers }),
        axios.get(`${API}/admin/pricing`, { headers }),
        axios.get(`${API}/admin/coupons`, { headers })
      ]);
      setSettings(settingsRes.data);
      setPlans(plansRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const getHeaders = () => ({ 'Authorization': `Bearer ${adminToken}` });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="mx-auto mb-6" style={{ maxWidth: '320px' }}>
              <BondAnimatedLogo variant="v3" />
            </div>
            <p className="text-center text-[#A3A3A3] text-sm uppercase tracking-[0.3em]">
              Pannello di Amministrazione
            </p>
          </div>
          
          {mfa ? <AdminMfa challenge={mfa} onToken={finishLogin} onBack={() => setMfa(null)} /> : (
          <form onSubmit={handleLogin} className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-8">
            {loginError && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-900/50 rounded text-red-400 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {loginError}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm text-[#E5E5E5] mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Inserisci username"
                className="input-smart rounded-none h-12"
                autoComplete="username"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-[#E5E5E5] mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci password"
                  className="input-smart rounded-none h-12 pr-12"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <Button type="submit" className="bond-btn bond-btn-primary w-full" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : 'Accedi'}
            </Button>
          </form>)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#0A0A0A] border-r border-[#1f2937] transition-all duration-300 flex flex-col sticky top-0 h-screen shrink-0`}>
        <div className="p-4 border-b border-[#1f2937]">
          {sidebarOpen ? (
            <BondAnimatedLogo variant="v3" style={{ width: '100%' }} />
          ) : (
            <div className="text-center">
              <span className="font-bold text-lg text-[#D4AF37] tracking-[0.2em]">B.</span>
            </div>
          )}
          <p className={`text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] mt-3 ${sidebarOpen ? 'text-center' : 'hidden'}`}>
            Admin Panel
          </p>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {sidebarOpen && <li className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]" data-testid="admin-group-bond">Portale BOND</li>}
            {PORTAL_TABS.map(([id, Icon, label]) => (
              <NavItem key={id} icon={Icon} label={label} active={activeTab === id} onClick={() => setActiveTab(id)} collapsed={!sidebarOpen} />
            ))}
            {sidebarOpen && <li className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]" data-testid="admin-group-eddi">EDDI · piani tariffari</li>}
            <NavItem 
              icon={Settings} 
              label="Impostazioni EDDI" 
              active={activeTab === 'settings'} 
              onClick={() => setActiveTab('settings')}
              collapsed={!sidebarOpen}
            />
            <NavItem 
              icon={Monitor} 
              label="Preview" 
              active={activeTab === 'preview'} 
              onClick={() => setActiveTab('preview')}
              collapsed={!sidebarOpen}
            />
            <NavItem 
              icon={CreditCard} 
              label="Piani EDDI" 
              active={activeTab === 'plans'} 
              onClick={() => setActiveTab('plans')}
              collapsed={!sidebarOpen}
            />
            <NavItem 
              icon={Tag} 
              label="Coupon EDDI" 
              active={activeTab === 'coupons'} 
              onClick={() => setActiveTab('coupons')}
              collapsed={!sidebarOpen}
            />
            <NavItem 
              icon={Users} 
              label="Ordini EDDI" 
              active={activeTab === 'orders'} 
              onClick={() => setActiveTab('orders')}
              collapsed={!sidebarOpen}
            />
            {sidebarOpen && <li className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Generale</li>}
            <NavItem 
              icon={Shield} 
              label="Sicurezza" 
              active={activeTab === 'security'} 
              onClick={() => setActiveTab('security')}
              collapsed={!sidebarOpen}
            />
          </ul>
        </nav>

        <div className="p-4 border-t border-smartGrey-border">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 text-smartGrey-muted hover:text-red-500 transition-colors w-full ${!sidebarOpen ? 'justify-center' : ''}`}
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Esci</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-smartBlack-card border-b border-smartGrey-border px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-smartGrey-muted hover:text-white"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h2 className="font-heading font-semibold text-lg text-smartGrey-text">
              {PORTAL_TABS.find(([id]) => id === activeTab)?.[2]}
              {activeTab === 'settings' && 'Impostazioni EDDI'}
              {activeTab === 'preview' && 'Preview Piattaforma'}
              {activeTab === 'plans' && 'Gestione Piani'}
              {activeTab === 'coupons' && 'Gestione Coupon'}
              {activeTab === 'orders' && 'Ordini'}
              {activeTab === 'security' && 'Sicurezza & Eventi Sospetti'}
            </h2>
          </div>
          <a 
            href="/" 
            target="_blank" 
            className="text-sm text-smartGrey-muted hover:text-smartRed"
          >
            Vai al sito →
          </a>
        </header>

        {/* Content */}
        <div className="p-6">
          <AdminPasskeyPrompt token={adminToken} />
          {activeTab === 'settings' && <AdminBiometricCard token={adminToken} />}
          {activeTab === 'settings' && (
            <SettingsTab 
              settings={settings} 
              setSettings={setSettings} 
              getHeaders={getHeaders}
            />
          )}
          {activeTab === 'preview' && (
            <PreviewTab 
              settings={settings} 
              setSettings={setSettings} 
              getHeaders={getHeaders}
            />
          )}
          {activeTab === 'plans' && (
            <PlansTab 
              plans={plans} 
              setPlans={setPlans} 
              getHeaders={getHeaders}
              onRefresh={() => loadAllData(adminToken)}
            />
          )}
          {activeTab === 'coupons' && (
            <CouponsTab 
              coupons={coupons} 
              setCoupons={setCoupons} 
              plans={plans}
              getHeaders={getHeaders}
              onRefresh={() => loadAllData(adminToken)}
            />
          )}
          {activeTab === 'orders' && (
            <OrdersTab getHeaders={getHeaders} />
          )}
          {activeTab === 'security' && (
            <SecurityEventsTab getHeaders={getHeaders} />
          )}
          {activeTab === 'clients' && (
            <AdminClients getHeaders={getHeaders} />
          )}
          {PORTAL_TABS.some(([id]) => id === activeTab) && activeTab !== 'clients' && (
            <AdminPortalTab tab={activeTab} getHeaders={getHeaders} />
          )}
        </div>
      </main>
    </div>
  );
}

// Navigation Item Component
const PORTAL_TABS = [
  ['clients', Users, 'Clienti, plafond e abilitazioni'], ['prices', Tag, 'Listino prezzi e canoni'], ['requests', Mail, 'Richieste servizi'],
  ['wb', Shield, 'Whistleblowing'], ['amlapi', Link, 'API AML clienti'], ['statements', CreditCard, 'Estratti conto'],
  ['sister', Server, 'Visure catastali (SISTER)'], ['consumption', BarChart3, 'Consumi del mese'], ['wallets', Gauge, 'Plafond e crediti'], ['providers', Receipt, 'Costi fornitori'], ['foreign', Globe, 'Investigazioni estere'], ['guide', BookOpen, 'Guida portale'], ['portalsettings', Settings, 'Impostazioni portale'],
];

function NavItem({ icon: Icon, label, active, onClick, collapsed }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
          active 
            ? 'bg-smartRed/10 text-smartRed' 
            : 'text-smartGrey-muted hover:bg-smartBlack-surface hover:text-white'
        } ${collapsed ? 'justify-center' : ''}`}
      >
        <Icon size={20} />
        {!collapsed && <span>{label}</span>}
      </button>
    </li>
  );
}

// Settings Tab
function SettingsTab({ settings, setSettings, getHeaders }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field, value) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, localSettings, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      setSettings(response.data);
      toast.success('Impostazioni salvate!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8">
      {/* General Settings */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">Impostazioni Generali</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">URL Login Piattaforma (OsintLab)</label>
            <Input
              value={localSettings.osintlab_login_url || ''}
              onChange={(e) => handleChange('osintlab_login_url', e.target.value)}
              placeholder="https://osintlab.it/it/auth/login"
              className="input-smart rounded-none h-11"
            />
            <p className="text-xs text-smartGrey-muted mt-1">URL dove vengono reindirizzati gli utenti per accedere</p>
          </div>
          
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Nome Azienda</label>
            <Input
              value={localSettings.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              className="input-smart rounded-none h-11"
            />
          </div>
        </div>
      </section>

      {/* Email Settings */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">Impostazioni Email</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Email Amministrazione</label>
            <Input
              type="email"
              value={localSettings.admin_email || ''}
              onChange={(e) => handleChange('admin_email', e.target.value)}
              placeholder="info@smart-info.it"
              className="input-smart rounded-none h-11"
            />
            <p className="text-xs text-smartGrey-muted mt-1">Le notifiche degli ordini verranno inviate qui</p>
          </div>
        </div>
      </section>

      {/* SMTP Settings */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Server size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text flex-1">Configurazione SMTP (riserva)</h3>
          <Button type="button" onClick={async () => {
            try { const { data } = await axios.post(`${API}/admin/settings/test-email`, null, { headers: getHeaders() }); toast.success(`Email di prova inviata a ${data.to} tramite ${({ resend: 'Resend', smtp: 'SMTP', proxy: 'servizio Emergent' })[data.channel]}`); }
            catch (e) { toast.error(e?.response?.data?.detail || 'Invio non riuscito'); }
          }} className="h-9 rounded-none bg-transparent border border-smartGrey-border text-smartGrey-text" data-testid="admin-test-email">Invia email di prova</Button>
        </div>
        <p className="text-xs text-smartGrey-muted -mt-4 mb-4">Le email partono da Resend (noreply@app.smart-info.it). Se Resend non è disponibile, BOND usa questo server SMTP. Salva prima di inviare la prova.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Server SMTP</label>
            <Input
              value={localSettings.smtp_host || ''}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              placeholder="smtp.example.com"
              className="input-smart rounded-none h-11"
            />
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Porta</label>
            <Input
              type="number"
              value={localSettings.smtp_port || 587}
              onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
              className="input-smart rounded-none h-11"
            />
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Username SMTP</label>
            <Input
              value={localSettings.smtp_user || ''}
              onChange={(e) => handleChange('smtp_user', e.target.value)}
              className="input-smart rounded-none h-11"
            />
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Password SMTP</label>
            <div className="relative">
              <Input
                type={showSmtpPassword ? 'text' : 'password'}
                value={localSettings.smtp_password || ''}
                onChange={(e) => handleChange('smtp_password', e.target.value)}
                className="input-smart rounded-none h-11 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-smartGrey-muted hover:text-white"
              >
                {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Email Mittente</label>
            <Input
              type="email"
              value={localSettings.smtp_from_email || ''}
              onChange={(e) => handleChange('smtp_from_email', e.target.value)}
              placeholder="noreply@smart-info.it"
              className="input-smart rounded-none h-11"
            />
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">Nome Mittente</label>
            <Input
              value={localSettings.smtp_from_name || ''}
              onChange={(e) => handleChange('smtp_from_name', e.target.value)}
              placeholder="BOND by Smart Info"
              className="input-smart rounded-none h-11"
            />
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-3">
          <Checkbox
            id="smtp_tls"
            checked={localSettings.smtp_use_tls !== false}
            onCheckedChange={(checked) => handleChange('smtp_use_tls', checked)}
          />
          <label htmlFor="smtp_tls" className="text-sm text-smartGrey-muted">Usa TLS</label>
        </div>
      </section>

      {/* EDDI/OsintLab API */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle size={20} className="text-green-500" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">API EDDI/OsintLab</h3>
          <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded">ATTIVA</span>
        </div>
        
        <div className="bg-green-500/10 border border-green-500/30 rounded-md p-4 mb-4">
          <p className="text-sm text-green-400">
            Integrazione EDDI attiva. Gli utenti vengono automaticamente creati sulla piattaforma EDDI al momento della registrazione.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">URL API EDDI</label>
            <Input
              value={localSettings.eddi_api_url || 'https://smartinfo-api.test.osintlab.it'}
              onChange={(e) => handleChange('eddi_api_url', e.target.value)}
              placeholder="https://smartinfo-api.test.osintlab.it"
              className="input-smart rounded-none h-11"
            />
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">API Key EDDI</label>
            <Input
              type="password"
              value={localSettings.eddi_api_key || ''}
              onChange={(e) => handleChange('eddi_api_key', e.target.value)}
              placeholder="••••••••••••••••"
              className="input-smart rounded-none h-11"
            />
            <p className="text-xs text-smartGrey-text mt-1">La chiave API è configurata nel file .env del backend</p>
          </div>
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">URL Callback EDDI</label>
            <Input
              value={localSettings.eddi_callback_url || 'https://smartinfo-app.test.osintlab.it/auth/smartinfo-callback'}
              onChange={(e) => handleChange('eddi_callback_url', e.target.value)}
              placeholder="https://smartinfo-app.test.osintlab.it/auth/smartinfo-callback"
              className="input-smart rounded-none h-11"
            />
          </div>
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bond-btn bond-btn-primary"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          SALVA IMPOSTAZIONI
        </Button>
      </div>
    </div>
  );
}

// Preview Tab
function PreviewTab({ settings, setSettings, getHeaders }) {
  const [localSettings, setLocalSettings] = useState({
    preview_type: settings.preview_type || 'image',
    preview_url: settings.preview_url || '',
    preview_title_it: settings.preview_title_it || 'Scopri la Piattaforma',
    preview_title_en: settings.preview_title_en || 'Discover the Platform',
    preview_description_it: settings.preview_description_it || '',
    preview_description_en: settings.preview_description_en || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSettings({
      preview_type: settings.preview_type || 'image',
      preview_url: settings.preview_url || '',
      preview_title_it: settings.preview_title_it || 'Scopri la Piattaforma',
      preview_title_en: settings.preview_title_en || 'Discover the Platform',
      preview_description_it: settings.preview_description_it || '',
      preview_description_en: settings.preview_description_en || ''
    });
  }, [settings]);

  const handleChange = (field, value) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, localSettings, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      setSettings(response.data);
      toast.success('Preview aggiornata!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Get YouTube thumbnail for preview
  const getYouTubeThumbnail = (url) => {
    if (!url) return '';
    let videoId = '';
    if (url.includes('youtube.com/watch')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return '';
  };

  const isVideo = localSettings.preview_type === 'video' || 
    localSettings.preview_url?.includes('youtube.com') || 
    localSettings.preview_url?.includes('youtu.be') ||
    localSettings.preview_url?.includes('vimeo.com');

  return (
    <div className="max-w-3xl space-y-8">
      {/* Preview Type Selection */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Monitor size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">Tipo di Preview</h3>
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={() => handleChange('preview_type', 'image')}
            className={`flex-1 p-4 rounded-md border-2 transition-colors ${
              localSettings.preview_type === 'image' 
                ? 'border-smartRed bg-smartRed/10' 
                : 'border-smartGrey-border hover:border-smartGrey-muted'
            }`}
          >
            <Image size={32} className={`mx-auto mb-2 ${localSettings.preview_type === 'image' ? 'text-smartRed' : 'text-smartGrey-muted'}`} />
            <p className={`text-sm font-medium ${localSettings.preview_type === 'image' ? 'text-smartRed' : 'text-smartGrey-text'}`}>
              Immagine
            </p>
            <p className="text-xs text-smartGrey-muted mt-1">Screenshot o grafica</p>
          </button>
          
          <button
            onClick={() => handleChange('preview_type', 'video')}
            className={`flex-1 p-4 rounded-md border-2 transition-colors ${
              localSettings.preview_type === 'video' 
                ? 'border-smartRed bg-smartRed/10' 
                : 'border-smartGrey-border hover:border-smartGrey-muted'
            }`}
          >
            <Video size={32} className={`mx-auto mb-2 ${localSettings.preview_type === 'video' ? 'text-smartRed' : 'text-smartGrey-muted'}`} />
            <p className={`text-sm font-medium ${localSettings.preview_type === 'video' ? 'text-smartRed' : 'text-smartGrey-text'}`}>
              Video
            </p>
            <p className="text-xs text-smartGrey-muted mt-1">YouTube, Vimeo o MP4</p>
          </button>
        </div>
      </section>

      {/* Preview URL */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">URL Preview</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-smartGrey-text mb-2">
              {localSettings.preview_type === 'video' ? 'URL Video (YouTube, Vimeo, MP4)' : 'URL Immagine'}
            </label>
            <Input
              value={localSettings.preview_url || ''}
              onChange={(e) => handleChange('preview_url', e.target.value)}
              placeholder={localSettings.preview_type === 'video' 
                ? 'https://www.youtube.com/watch?v=...' 
                : 'https://example.com/screenshot.png'
              }
              className="input-smart rounded-none h-11"
            />
            <p className="text-xs text-smartGrey-muted mt-1">
              {localSettings.preview_type === 'video' 
                ? 'Supporta: YouTube, Vimeo, link diretti a file .mp4 o .webm' 
                : 'Inserisci l\'URL diretto dell\'immagine (PNG, JPG, WebP)'
              }
            </p>
          </div>

          {/* Preview thumbnail */}
          {localSettings.preview_url && (
            <div className="mt-4">
              <p className="text-sm text-smartGrey-text mb-2">Anteprima:</p>
              <div className="relative aspect-video max-w-md rounded-md overflow-hidden bg-smartBlack-surface border border-smartGrey-border">
                {isVideo && (localSettings.preview_url.includes('youtube') || localSettings.preview_url.includes('youtu.be')) ? (
                  <img 
                    src={getYouTubeThumbnail(localSettings.preview_url)} 
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : !isVideo ? (
                  <img 
                    src={localSettings.preview_url} 
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video size={48} className="text-smartGrey-muted" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Preview Text */}
      <section className="card-smart rounded-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <Edit size={20} className="text-smartRed" />
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text">Testi Sezione Preview</h3>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-smartGrey-text mb-2">Titolo (Italiano)</label>
              <Input
                value={localSettings.preview_title_it || ''}
                onChange={(e) => handleChange('preview_title_it', e.target.value)}
                placeholder="Scopri la Piattaforma"
                className="input-smart rounded-none h-11"
              />
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-2">Titolo (English)</label>
              <Input
                value={localSettings.preview_title_en || ''}
                onChange={(e) => handleChange('preview_title_en', e.target.value)}
                placeholder="Discover the Platform"
                className="input-smart rounded-none h-11"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-smartGrey-text mb-2">Descrizione (Italiano)</label>
              <Textarea
                value={localSettings.preview_description_it || ''}
                onChange={(e) => handleChange('preview_description_it', e.target.value)}
                placeholder="Guarda come BOND può trasformare le tue ricerche..."
                className="input-smart rounded-none min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-2">Descrizione (English)</label>
              <Textarea
                value={localSettings.preview_description_en || ''}
                onChange={(e) => handleChange('preview_description_en', e.target.value)}
                placeholder="See how BOND can transform your research..."
                className="input-smart rounded-none min-h-[100px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-4">
        <p className="text-sm text-blue-400">
          <strong>Nota:</strong> La sezione Preview apparirà sul sito solo quando inserisci un URL valido. 
          Se lasci l'URL vuoto, la sezione non verrà mostrata.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bond-btn bond-btn-primary"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          SALVA PREVIEW
        </Button>
      </div>
    </div>
  );
}

// Plans Tab
function PlansTab({ plans, setPlans, getHeaders, onRefresh }) {
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSavePlan = async (plan) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/pricing/${plan.id}`, {
        price: parseFloat(plan.price),
        original_price: plan.original_price ? parseFloat(plan.original_price) : null,
        name_it: plan.name_it,
        name_en: plan.name_en,
        description_it: plan.description_it,
        description_en: plan.description_en,
        features_it: plan.features_it,
        features_en: plan.features_en,
        popular: plan.popular,
        active: plan.active
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      toast.success('Piano aggiornato!');
      onRefresh();
      setEditingPlan(null);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {plans.map((plan) => (
        <div key={plan.id} className="card-smart rounded-md p-6">
          {editingPlan?.id === plan.id ? (
            <PlanEditForm 
              plan={editingPlan} 
              setPlan={setEditingPlan}
              onSave={() => handleSavePlan(editingPlan)}
              onCancel={() => setEditingPlan(null)}
              saving={saving}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading font-semibold text-lg text-smartGrey-text">{plan.name_it}</h3>
                  {plan.popular && (
                    <span className="text-xs bg-smartRed text-white px-2 py-0.5">POPOLARE</span>
                  )}
                  {!plan.active && (
                    <span className="text-xs bg-gray-500 text-white px-2 py-0.5">DISATTIVO</span>
                  )}
                </div>
                <p className="text-sm text-smartGrey-muted">{plan.description_it}</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  {plan.original_price && (
                    <p className="text-sm text-smartGrey-muted line-through">€{plan.original_price}</p>
                  )}
                  <p className="font-heading font-bold text-2xl text-smartGrey-text">
                    {plan.price === 0 ? 'Gratis' : `€${plan.price}`}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setEditingPlan({...plan})}
                  className="bond-btn bond-btn-secondary"
                >
                  <Edit size={16} className="mr-2" />
                  Modifica
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Plan Edit Form
function PlanEditForm({ plan, setPlan, onSave, onCancel, saving }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-smartGrey-text mb-1">Nome (IT)</label>
          <Input
            value={plan.name_it}
            onChange={(e) => setPlan({...plan, name_it: e.target.value})}
            className="input-smart rounded-none h-10"
          />
        </div>
        <div>
          <label className="block text-sm text-smartGrey-text mb-1">Nome (EN)</label>
          <Input
            value={plan.name_en}
            onChange={(e) => setPlan({...plan, name_en: e.target.value})}
            className="input-smart rounded-none h-10"
          />
        </div>
        <div>
          <label className="block text-sm text-smartGrey-text mb-1">Prezzo (€)</label>
          <Input
            type="number"
            step="0.01"
            value={plan.price}
            onChange={(e) => setPlan({...plan, price: e.target.value})}
            className="input-smart rounded-none h-10"
          />
        </div>
        <div>
          <label className="block text-sm text-smartGrey-text mb-1">Prezzo Originale (€)</label>
          <Input
            type="number"
            step="0.01"
            value={plan.original_price || ''}
            onChange={(e) => setPlan({...plan, original_price: e.target.value})}
            placeholder="Per mostrare prezzo barrato"
            className="input-smart rounded-none h-10"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-smartGrey-text mb-1">Descrizione (IT)</label>
          <Input
            value={plan.description_it}
            onChange={(e) => setPlan({...plan, description_it: e.target.value})}
            className="input-smart rounded-none h-10"
          />
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={plan.popular}
            onCheckedChange={(checked) => setPlan({...plan, popular: checked})}
          />
          <label className="text-sm text-smartGrey-muted">Popolare</label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={plan.active}
            onCheckedChange={(checked) => setPlan({...plan, active: checked})}
          />
          <label className="text-sm text-smartGrey-muted">Attivo</label>
        </div>
      </div>
      
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel} className="bond-btn bond-btn-secondary">
          Annulla
        </Button>
        <Button onClick={onSave} disabled={saving} className="bond-btn bond-btn-primary">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
          <span className="ml-2">Salva</span>
        </Button>
      </div>
    </div>
  );
}

// Coupons Tab
function CouponsTab({ coupons, setCoupons, plans, getHeaders, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: null,
    valid_plans: []
  });

  const handleCreateCoupon = async () => {
    if (!newCoupon.code || !newCoupon.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/coupons`, newCoupon, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      toast.success('Coupon creato!');
      onRefresh();
      setShowForm(false);
      setNewCoupon({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: null,
        valid_plans: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!confirm('Sei sicuro di voler eliminare questo coupon?')) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: getHeaders()
      });
      toast.success('Coupon eliminato');
      onRefresh();
    } catch (error) {
      toast.error('Errore nella eliminazione');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/admin/coupons/${coupon.id}`, {
        active: !coupon.active
      }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' }
      });
      onRefresh();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Coupon Button */}
      <div className="flex justify-between items-center">
        <p className="text-smartGrey-muted">{coupons.length} coupon totali</p>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bond-btn bond-btn-primary"
        >
          <Plus size={18} />
          Nuovo Coupon
        </Button>
      </div>

      {/* Create Coupon Form */}
      {showForm && (
        <div className="card-smart rounded-md p-6">
          <h3 className="font-heading font-semibold text-lg text-smartGrey-text mb-4">Crea Nuovo Coupon</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-smartGrey-text mb-1">Codice *</label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                placeholder="SCONTO20"
                className="input-smart rounded-none h-10 uppercase"
              />
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-1">Descrizione *</label>
              <Input
                value={newCoupon.description}
                onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                placeholder="20% di sconto"
                className="input-smart rounded-none h-10"
              />
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-1">Tipo Sconto</label>
              <select
                value={newCoupon.discount_type}
                onChange={(e) => setNewCoupon({...newCoupon, discount_type: e.target.value})}
                className="input-smart rounded-none h-10 w-full bg-smartBlack-surface border border-smartGrey-border text-smartGrey-text"
              >
                <option value="percentage">Percentuale (%)</option>
                <option value="fixed">Importo Fisso (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-1">
                Valore {newCoupon.discount_type === 'percentage' ? '(%)' : '(€)'}
              </label>
              <Input
                type="number"
                value={newCoupon.discount_value}
                onChange={(e) => setNewCoupon({...newCoupon, discount_value: parseFloat(e.target.value)})}
                className="input-smart rounded-none h-10"
              />
            </div>
            <div>
              <label className="block text-sm text-smartGrey-text mb-1">Utilizzi Massimi</label>
              <Input
                type="number"
                value={newCoupon.max_uses || ''}
                onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                placeholder="Illimitati"
                className="input-smart rounded-none h-10"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowForm(false)} className="bond-btn bond-btn-secondary">
              Annulla
            </Button>
            <Button onClick={handleCreateCoupon} disabled={saving} className="bond-btn bond-btn-primary">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
              <span className="ml-2">Crea Coupon</span>
            </Button>
          </div>
        </div>
      )}

      {/* Coupons List */}
      <div className="space-y-3">
        {coupons.map((coupon) => (
          <div key={coupon.id} className="card-smart rounded-md p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-3 h-3 rounded-full ${coupon.active ? 'bg-green-500' : 'bg-gray-500'}`} />
              <div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-smartRed font-bold">{coupon.code}</code>
                  <span className="text-smartGrey-text">
                    {coupon.discount_type === 'percentage' 
                      ? `-${coupon.discount_value}%` 
                      : `-€${coupon.discount_value}`}
                  </span>
                </div>
                <p className="text-sm text-smartGrey-muted">{coupon.description}</p>
                <p className="text-xs text-smartGrey-muted">
                  Utilizzi: {coupon.current_uses}{coupon.max_uses ? `/${coupon.max_uses}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleToggleActive(coupon)}
                className="text-smartGrey-muted hover:text-white"
              >
                {coupon.active ? 'Disattiva' : 'Attiva'}
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleDeleteCoupon(coupon.id)}
                className="text-red-500 hover:text-red-400"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
        
        {coupons.length === 0 && (
          <div className="text-center py-12 text-smartGrey-muted">
            Nessun coupon creato
          </div>
        )}
      </div>
    </div>
  );
}

// Orders Tab
function OrdersTab({ getHeaders }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: getHeaders()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-smartRed" size={32} />
      </div>
    );
  }

  return (
    <div>
      {orders.length === 0 ? (
        <div className="text-center py-12 text-smartGrey-muted">
          Nessun ordine ancora
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="card-smart rounded-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-smartGrey-text font-medium">{order.user_email}</p>
                  <p className="text-sm text-smartGrey-muted">
                    {order.plan_id} - €{order.amount}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2 py-1 rounded ${
                    order.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {order.status.toUpperCase()}
                  </span>
                  <p className="text-xs text-smartGrey-muted mt-1">
                    {new Date(order.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============= SECURITY EVENTS TAB =============

const EVENT_LABELS = {
  'auth.brute_force.ip_lockout': { label: 'Lockout IP (brute-force)', icon: '🚫' },
  'auth.brute_force.global_lockout': { label: 'Lockout globale email', icon: '🛡️' },
  'webhook.stripe.signature_failed': { label: 'Firma Stripe FALLITA', icon: '⚠️' },
  'webhook.stripe.signature_missing': { label: 'Webhook Stripe senza firma', icon: '⚠️' },
  'auth.eddi.legacy_fallback': { label: 'Login EDDI legacy', icon: '🕰️' },
  'auth.password_reset.success': { label: 'Reset password completato', icon: '🔑' },
};

const SEVERITY_STYLES = {
  high: 'bg-red-900/30 text-red-400 border-red-900/50',
  medium: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50',
  low: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
};

function SecurityEventsTab({ getHeaders }) {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterType, setFilterType] = useState('');

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterSeverity) params.set('severity', filterSeverity);
      if (filterType) params.set('event_type', filterType);
      const res = await axios.get(`${API}/admin/security-events?${params}`, {
        headers: getHeaders(),
      });
      setEvents(res.data.items || []);
      setSummary(res.data.summary_24h || {});
    } catch (err) {
      toast.error('Errore caricamento eventi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeverity, filterType]);

  return (
    <div className="space-y-6" data-testid="security-events-tab">
      {/* Header + refresh */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-smartGrey-muted max-w-2xl">
          Eventi sospetti rilevati negli ultimi <strong>90 giorni</strong>. Vengono registrati automaticamente quando si verificano lockout brute-force, fallimenti di firma Stripe e accessi tramite il fallback EDDI legacy.
        </p>
        <Button
          onClick={loadEvents}
          disabled={loading}
          className="bg-[#D4AF37] hover:bg-[#B8941F] text-black h-10 rounded-none"
          data-testid="reload-security-events-btn"
        >
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* 24h summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.keys(EVENT_LABELS).map((type) => (
          <div key={type} className="bg-smartBlack-card border border-smartGrey-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">{EVENT_LABELS[type].icon}</span>
              <span className="text-3xl font-bold text-[#D4AF37]">{summary[type] || 0}</span>
            </div>
            <p className="text-xs text-smartGrey-muted">{EVENT_LABELS[type].label}</p>
            <p className="text-[10px] text-smartGrey-muted mt-1 uppercase tracking-wider">Ultime 24h</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-smartBlack-card border border-smartGrey-border p-4 flex flex-wrap gap-3 items-center">
        <span className="text-xs text-smartGrey-muted uppercase tracking-wider">Filtri:</span>
        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="bg-[#050505] border border-smartGrey-border text-smartGrey-text text-sm px-3 py-2 rounded-none"
          data-testid="filter-severity"
        >
          <option value="">Tutte le severity</option>
          <option value="high">Solo HIGH</option>
          <option value="medium">Solo MEDIUM</option>
          <option value="low">Solo LOW</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-[#050505] border border-smartGrey-border text-smartGrey-text text-sm px-3 py-2 rounded-none"
          data-testid="filter-type"
        >
          <option value="">Tutti i tipi di evento</option>
          {Object.entries(EVENT_LABELS).map(([type, info]) => (
            <option key={type} value={type}>{info.label}</option>
          ))}
        </select>
        {(filterSeverity || filterType) && (
          <button
            onClick={() => { setFilterSeverity(''); setFilterType(''); }}
            className="text-xs text-smartGrey-muted hover:text-smartGrey-text underline"
          >
            Pulisci filtri
          </button>
        )}
      </div>

      {/* Events list */}
      <div className="bg-smartBlack-card border border-smartGrey-border">
        {loading ? (
          <div className="p-12 text-center text-smartGrey-muted">
            <Loader2 size={32} className="animate-spin mx-auto mb-3" />
            Caricamento eventi...
          </div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-smartGrey-muted">
            <Shield size={32} className="mx-auto mb-3 opacity-50" />
            Nessun evento registrato {filterSeverity || filterType ? 'con i filtri attuali' : 'negli ultimi 90 giorni'}.
          </div>
        ) : (
          <div className="divide-y divide-smartGrey-border">
            {events.map((evt) => {
              const info = EVENT_LABELS[evt.type] || { label: evt.type, icon: '❔' };
              const sevClass = SEVERITY_STYLES[evt.severity] || SEVERITY_STYLES.low;
              return (
                <div key={evt.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="text-xl mt-0.5">{info.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-smartGrey-text">{info.label}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${sevClass}`}>
                          {evt.severity}
                        </span>
                        <span className="text-xs text-smartGrey-muted">
                          {new Date(evt.created_at).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit',
                          })}
                        </span>
                      </div>
                      <div className="text-xs text-smartGrey-muted flex flex-wrap gap-x-4 gap-y-1">
                        {evt.source_ip && (
                          <span><span className="opacity-60">IP:</span> <code className="text-[#D4AF37]">{evt.source_ip}</code></span>
                        )}
                        {evt.user_email && (
                          <span><span className="opacity-60">Utente:</span> <code className="text-[#D4AF37]">{evt.user_email}</code></span>
                        )}
                        {evt.details && Object.keys(evt.details).length > 0 && (
                          <details className="cursor-pointer">
                            <summary className="opacity-60 hover:opacity-100">Dettagli</summary>
                            <pre className="text-[10px] mt-1 p-2 bg-[#050505] border border-smartGrey-border overflow-x-auto">
{JSON.stringify(evt.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-smartGrey-muted text-center">
        Totale eventi caricati: <strong>{events.length}</strong> · Auto-cancellazione dopo 90 giorni (TTL MongoDB)
      </p>
    </div>
  );
}
